import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, CheckCircle, Clock, Star, Zap, Shield, Package,
  Smartphone, MapPin, Phone, Mail, Instagram, ChevronDown,
  Repeat, Sparkles, TrendingUp, Timer, Award
} from 'lucide-react';

// ─── Hooks ──────────────────────────────────────────────────────────────────

const useInView = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); observer.disconnect(); }
    }, { threshold: 0.12 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, inView];
};

const useCountUp = (target, duration = 1400, start = false) => {
  const [count, setCount] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    if (!start) return;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(Math.round(eased * target));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration, start]);
  return count;
};

// ─── FadeIn wrapper ──────────────────────────────────────────────────────────
const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateY(0)' : 'translateY(28px)',
      transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
    }}>
      {children}
    </div>
  );
};

// ─── Stat card with count-up ─────────────────────────────────────────────────
const StatCard = ({ value, label, delay, bg, text, border }) => {
  const [ref, inView] = useInView();
  const [started, setStarted] = useState(false);
  const numMatch = value.match(/[\d.]+/);
  const num = numMatch ? parseFloat(numMatch[0]) : 0;
  const prefix = num > 0 ? value.split(numMatch[0])[0] : '';
  const suffix = num > 0 ? value.slice(value.indexOf(numMatch[0]) + numMatch[0].length) : value;
  const count = useCountUp(num, 1300, started);
  useEffect(() => { if (inView) setTimeout(() => setStarted(true), delay); }, [inView, delay]);
  const display = num > 0 ? `${prefix}${Number.isInteger(num) ? count : count.toFixed(1)}${suffix}` : value;

  return (
    <div ref={ref} className={`rounded-2xl p-5 text-center border ${bg} ${border}`}
      style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)', transition: `all 0.55s ease ${delay}ms` }}>
      <div className={`text-2xl font-black ${text}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{display}</div>
      <div className="text-sm text-slate-500 mt-1 font-medium">{label}</div>
    </div>
  );
};

// ─── Workflow video sequence ──────────────────────────────────────────────────
const workflowVideos = [
  {
    src: '/videos/pickup.mp4',
    stage: '01', label: 'Pickup', desc: 'We collect from your home',
    accent: 'bg-blue-500',
  },
  {
    src: 'https://assets.mixkit.co/videos/47111/47111-720.mp4',
    stage: '02', label: 'Expert Wash', desc: 'Professional deep cleaning',
    accent: 'bg-orange-500',
  },
  {
    src: 'https://assets.mixkit.co/videos/18729/18729-720.mp4',
    stage: '03', label: 'Fold & Press', desc: 'Neat, pressed and ready',
    accent: 'bg-violet-500',
  },
  {
    src: 'https://assets.mixkit.co/videos/31155/31155-720.mp4',
    stage: '04', label: 'Delivery', desc: 'Back at your doorstep',
    accent: 'bg-emerald-500',
  },
];

// ─── Landing Page ────────────────────────────────────────────────────────────
const LandingPage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [videoIndex, setVideoIndex] = useState(0);
  const [videoVisible, setVideoVisible] = useState(true);
  const videoRefs = useRef([]);

  // Cycle through workflow videos every 6 seconds
  useEffect(() => {
    const cycle = setInterval(() => {
      setVideoVisible(false);
      setTimeout(() => {
        setVideoIndex(i => {
          const next = (i + 1) % workflowVideos.length;
          return next;
        });
        setVideoVisible(true);
      }, 700);
    }, 6000);
    return () => clearInterval(cycle);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onMove = (e) => setMousePos({
      x: (e.clientX / window.innerWidth - 0.5) * 2,
      y: (e.clientY / window.innerHeight - 0.5) * 2,
    });
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  useEffect(() => {
    const adminToken = localStorage.getItem('token');
    const customerToken = localStorage.getItem('customerToken');
    if (adminToken) navigate('/dashboard');
    else if (customerToken) navigate('/customer/dashboard');
  }, [navigate]);

  const plans = [
    {
      name: 'FoldPass',
      badge: 'Most Popular',
      icon: Repeat,
      desc: 'Monthly subscription with fixed weight. Perfect for predictable costs and regular service.',
      features: ['10kg, 20kg & 40kg plans', 'Flexible scheduling', 'Priority pickup', 'Fabric care report'],
      cta: 'See FoldPass Plans',
      href: '/pricing#foldpass',
      color: 'blue',
      bg: 'bg-blue-600',
      cardBg: 'bg-gradient-to-br from-blue-600 to-blue-700',
      featured: true,
    },
    {
      name: 'FlexFold',
      badge: 'Pay per wash',
      icon: Package,
      desc: 'Pay per wash with 72-hour delivery. No subscriptions, no commitments — just laundry when you need it.',
      features: ['Doorstep pickup & delivery', '₹79/kg washing + ironing', 'No minimum weight', 'Pay per wash'],
      cta: 'See FlexFold Pricing',
      href: '/pricing#laundry',
      color: 'violet',
      bg: 'bg-violet-600',
      cardBg: 'bg-white',
      featured: false,
    },
    {
      name: 'FlashFold',
      badge: 'Express',
      icon: Zap,
      desc: 'Same-day or 24-hour express service for when you need clean clothes in a hurry.',
      features: ['24-hour turnaround', '₹99/kg express pricing', 'SMS updates', 'Premium care'],
      cta: 'See FlashFold Pricing',
      href: '/pricing#laundry',
      color: 'orange',
      bg: 'bg-orange-500',
      cardBg: 'bg-white',
      featured: false,
    },
    {
      name: 'Dry Clean',
      badge: 'Premium',
      icon: Sparkles,
      desc: 'Professional dry cleaning for delicate garments. Suits, sarees, lehengas and more — handled with expert care.',
      features: ['100+ garment types', 'Women, men & kids', 'Stain treatment', 'Gentle fabric care'],
      cta: 'See Dry Clean Prices',
      href: '/pricing#dry-clean',
      color: 'emerald',
      bg: 'bg-emerald-500',
      cardBg: 'bg-white',
      featured: false,
    },
  ];

  const steps = [
    { step: '01', title: 'Book Your Pickup', desc: 'Schedule via the app in under 60 seconds.', icon: Smartphone, color: 'bg-blue-100 text-blue-600', num: 'text-blue-100' },
    { step: '02', title: 'We Collect', desc: 'Our team arrives at your home at the scheduled time.', icon: Package, color: 'bg-orange-100 text-orange-600', num: 'text-orange-100' },
    { step: '03', title: 'Expert Cleaning', desc: 'Special attention to collar, cuff, and fabric care.', icon: Sparkles, color: 'bg-violet-100 text-violet-600', num: 'text-violet-100' },
    { step: '04', title: 'Fresh Delivery', desc: 'Clean clothes delivered back to your doorstep.', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-600', num: 'text-emerald-100' },
  ];

  const features = [
    { icon: Clock, title: '72-Hour Cycle', desc: 'Pickup and delivery twice a week — never run out of clean clothes.', color: 'bg-blue-50 text-blue-600 border-blue-100' },
    { icon: Shield, title: 'Fabric Care', desc: 'Special attention to collar, cuff, and delicate fabrics.', color: 'bg-orange-50 text-orange-600 border-orange-100' },
    { icon: TrendingUp, title: 'No Hidden Costs', desc: 'Transparent pricing with no surprise charges. What you see is what you pay.', color: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
    { icon: MapPin, title: 'Gurgaon Local', desc: 'Built for residents across Gurgaon — doorstep pickup and delivery.', color: 'bg-violet-50 text-violet-600 border-violet-100' },
    { icon: Timer, title: 'Express Available', desc: 'FlashFold gets your laundry done in 24 hours for urgent situations.', color: 'bg-amber-50 text-amber-600 border-amber-100' },
    { icon: Award, title: 'Premium Quality', desc: 'Professional machines and processes. Clothes come back fresh and pressed.', color: 'bg-rose-50 text-rose-600 border-rose-100' },
  ];

  const stats = [
    { value: '500+', label: 'Happy Residents', bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-100' },
    { value: '4.8★', label: 'Average Rating', bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-100' },
    { value: 'Patel Nagar', label: 'Gurgaon', bg: 'bg-orange-50', text: 'text-orange-500', border: 'border-orange-100' },
    { value: '100%', label: 'Satisfaction', bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-100' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 overflow-x-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* ── NAV ── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <img
            src={scrolled ? '/logo.png' : '/justfold-logo-transparent.png'}
            alt="JustFold"
            className="h-9 transition-all duration-300 object-contain"
            style={{ filter: scrolled ? 'none' : 'brightness(0) invert(1)' }}
          />
          <div className="hidden md:flex items-center gap-8">
            {[['services', 'Services'], ['how-it-works', 'How it Works'], ['features', 'Features'], ['contact', 'Contact']].map(([id, label]) => (
              <a key={id} href={`#${id}`}
                className={`text-sm font-medium transition-colors relative group ${scrolled ? 'text-slate-600 hover:text-blue-600' : 'text-white/80 hover:text-white'}`}>
                {label}
                <span className={`absolute -bottom-0.5 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full rounded-full ${scrolled ? 'bg-blue-600' : 'bg-white'}`} />
              </a>
            ))}
            <button onClick={() => navigate('/pricing')}
              className={`text-sm font-medium transition-colors relative group ${scrolled ? 'text-slate-600 hover:text-blue-600' : 'text-white/80 hover:text-white'}`}>
              Pricing
              <span className={`absolute -bottom-0.5 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full rounded-full ${scrolled ? 'bg-blue-600' : 'bg-white'}`} />
            </button>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button onClick={() => navigate('/login')}
              className={`text-sm font-medium transition-colors px-4 py-2 ${scrolled ? 'text-slate-500 hover:text-slate-800' : 'text-white/70 hover:text-white'}`}>
              Admin Login
            </button>
            <button
              onClick={() => navigate('/customer/login')}
              className="text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl transition-all hover:shadow-lg hover:shadow-blue-600/25 hover:-translate-y-0.5"
            >
              Book Now →
            </button>
          </div>
          <button className={`md:hidden ${scrolled ? 'text-slate-600' : 'text-white'}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {mobileMenuOpen
                ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
                : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>
              }
            </svg>
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 px-4 py-4 space-y-3">
            {[['services','Services'],['how-it-works','How it Works'],['features','Features'],['contact','Contact']].map(([id, label]) => (
              <a key={id} href={`#${id}`} className="block text-sm text-slate-600 py-2" onClick={() => setMobileMenuOpen(false)}>{label}</a>
            ))}
            <button onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }} className="block text-sm text-slate-600 py-2 w-full text-left">Pricing</button>
            <div className="pt-2 flex flex-col gap-2">
              <button onClick={() => navigate('/customer/login')} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl">Book Now</button>
              <button onClick={() => navigate('/login')} className="w-full border border-slate-200 text-slate-600 py-3 rounded-xl text-sm">Admin Login</button>
            </div>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-4 pt-16 overflow-hidden bg-slate-950">

        {/* ── Video background — desktop only ── */}
        <div className="absolute inset-0 overflow-hidden">
          {workflowVideos.map((v, i) => (
            <video
              key={v.src}
              ref={el => videoRefs.current[i] = el}
              src={v.src}
              autoPlay
              muted
              loop
              playsInline
              className="absolute inset-0 w-full h-full object-cover hidden sm:block"
              style={{
                opacity: i === videoIndex ? (videoVisible ? 1 : 0) : 0,
                transition: 'opacity 0.7s ease-in-out',
              }}
            />
          ))}

          {/* Dark gradient overlay — keeps text readable */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/75 via-slate-900/55 to-slate-950/80" />

          {/* Subtle dot-grid texture */}
          <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

          {/* Color accent blobs — matches brand, visible through dark overlay */}
          <div className="absolute -top-20 -left-20 w-[500px] h-[500px] bg-blue-600 rounded-full blur-[140px] opacity-20 pointer-events-none"
            style={{ transform: `translate(${mousePos.x * 18}px, ${mousePos.y * 12}px)`, transition: 'transform 0.9s cubic-bezier(0.4,0,0.2,1)' }} />
          <div className="absolute top-1/3 right-0 w-[350px] h-[350px] bg-orange-500 rounded-full blur-[120px] opacity-15 pointer-events-none"
            style={{ transform: `translate(${mousePos.x * -12}px, ${mousePos.y * 8}px)`, transition: 'transform 1.1s cubic-bezier(0.4,0,0.2,1)' }} />
        </div>

        {/* ── Stage indicator — top left ── */}
        <div className="absolute top-20 left-6 z-20 hidden sm:flex flex-col gap-1.5">
          {workflowVideos.map((v, i) => (
            <button
              key={i}
              onClick={() => { setVideoVisible(false); setTimeout(() => { setVideoIndex(i); setVideoVisible(true); }, 300); }}
              className={`flex items-center gap-2.5 px-3 py-1.5 rounded-xl border transition-all duration-300 text-left ${
                i === videoIndex
                  ? 'bg-white/15 backdrop-blur-sm border-white/30 scale-105'
                  : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 opacity-60 hover:opacity-100'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${i === videoIndex ? v.accent : 'bg-white/40'}`} />
              <div>
                <p className="text-[10px] font-black text-white/50 uppercase tracking-widest leading-none">{v.stage}</p>
                <p className={`text-xs font-bold leading-tight ${i === videoIndex ? 'text-white' : 'text-white/60'}`}>{v.label}</p>
              </div>
            </button>
          ))}
        </div>

        {/* ── Current stage badge — animated ── */}
        <div className="absolute top-24 right-6 z-20 hidden md:block"
          style={{ opacity: videoVisible ? 1 : 0, transform: videoVisible ? 'translateX(0)' : 'translateX(20px)', transition: 'all 0.6s ease' }}>
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-4 py-3 text-left">
            <p className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-0.5">Now showing</p>
            <p className="text-sm font-bold text-white">{workflowVideos[videoIndex].label}</p>
            <p className="text-xs text-white/60">{workflowVideos[videoIndex].desc}</p>
          </div>
        </div>

        {/* ── Hero content ── */}
        <div className="relative z-10 max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-8"
            style={{ animation: 'fadeInDown 0.6s ease both' }}>
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-sm font-medium text-white/90">Now serving Gurgaon residents</span>
          </div>

          {/* Headline */}
          <h1 style={{ fontFamily: 'Manrope, sans-serif', animation: 'fadeInUp 0.8s ease 0.1s both' }}
            className="text-5xl sm:text-6xl md:text-7xl font-black leading-tight mb-6 text-white">
            Laundry that<br />
            <span className="bg-gradient-to-r from-blue-400 to-sky-300 bg-clip-text text-transparent">Actually Works</span>
            <br />for You
          </h1>

          <p className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed"
            style={{ animation: 'fadeInUp 0.8s ease 0.25s both' }}>
            Professional laundry service designed for everyone in Gurgaon.
            Premium quality at pocket-friendly prices.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
            style={{ animation: 'fadeInUp 0.8s ease 0.4s both' }}>
            <button
              onClick={() => navigate('/customer/login')}
              className="group inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold text-base px-8 py-4 rounded-2xl transition-all hover:shadow-xl hover:shadow-blue-500/40 hover:-translate-y-1"
            >
              Book Your First Pickup
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto"
            style={{ animation: 'fadeInUp 0.8s ease 0.55s both' }}>
            {stats.map((s, i) => (
              <div key={s.label} className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl p-4 text-center"
                style={{ opacity: 1, transition: `all 0.55s ease ${i * 80}ms` }}>
                <div className="text-2xl font-black text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.value}</div>
                <div className="text-xs text-white/60 mt-1 font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Bottom workflow progress bar — desktop only ── */}
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-20 items-center gap-2 hidden sm:flex">
          {workflowVideos.map((v, i) => (
            <button key={i} onClick={() => { setVideoVisible(false); setTimeout(() => { setVideoIndex(i); setVideoVisible(true); }, 300); }}
              className={`h-1.5 rounded-full transition-all duration-500 ${i === videoIndex ? `${v.accent} w-8` : 'bg-white/30 w-3'}`} />
          ))}
        </div>

        <a href="#services" className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 hover:text-white transition-colors z-20">
          <ChevronDown className="w-6 h-6 animate-bounce" />
        </a>
      </section>

      {/* ── SERVICES ── */}
      <section id="services" className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <FadeIn className="text-center mb-16">
            <span className="inline-block bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Our Plans</span>
            <h2 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-4xl sm:text-5xl font-black text-slate-900 mb-4">
              Laundry Plans Designed for<br />
              <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">Your Lifestyle</span>
            </h2>
            <p className="text-slate-500 text-lg max-w-xl mx-auto">From subscriptions to express dry cleaning — a plan that fits every need.</p>
          </FadeIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, i) => {
              const Icon = plan.icon;
              return (
                <FadeIn key={plan.name} delay={i * 80}>
                  <div className={`relative rounded-3xl p-7 flex flex-col h-full transition-all duration-300 hover:-translate-y-2 ${
                    plan.featured
                      ? `${plan.cardBg} text-white shadow-2xl shadow-blue-600/20`
                      : 'bg-white border-2 border-slate-100 hover:border-blue-100 hover:shadow-xl shadow-sm'
                  }`}>
                    {plan.featured && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-orange-400 text-white text-xs font-black px-4 py-1 rounded-full shadow-md">{plan.badge}</span>
                      </div>
                    )}
                    {!plan.featured && (
                      <span className={`inline-block self-start ${plan.bg} text-white text-xs font-bold px-3 py-1 rounded-full mb-4`}>{plan.badge}</span>
                    )}
                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-5 ${plan.featured ? 'bg-white/20' : plan.bg}`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 style={{ fontFamily: 'Manrope, sans-serif' }} className={`text-xl font-black mb-2 ${plan.featured ? 'text-white' : 'text-slate-900'}`}>{plan.name}</h3>
                    <p className={`text-sm leading-relaxed mb-5 ${plan.featured ? 'text-blue-100' : 'text-slate-500'}`}>{plan.desc}</p>
                    <ul className="space-y-2 mb-6 flex-1">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-center gap-2 text-sm">
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${plan.featured ? 'bg-white/20' : plan.bg}`}>
                            <CheckCircle className="w-2.5 h-2.5 text-white" />
                          </div>
                          <span className={plan.featured ? 'text-blue-50' : 'text-slate-600'}>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button onClick={() => navigate(plan.href)}
                      className={`w-full py-3 rounded-2xl font-bold text-sm transition-all ${plan.featured ? 'bg-white text-blue-600 hover:bg-blue-50 hover:shadow-lg' : `${plan.bg} text-white hover:opacity-90 hover:shadow-lg`}`}>
                      {plan.cta} →
                    </button>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── ADD-ONS ── */}
      <section className="py-14 px-4 bg-gradient-to-r from-orange-50 via-white to-blue-50 border-t border-b border-slate-100">
        <FadeIn>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <span className="text-xs font-bold text-orange-600 uppercase tracking-widest">Add-on Services</span>
              <h3 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-2xl font-black text-slate-900 mt-1">More than just laundry</h3>
              <p className="text-slate-500 text-sm mt-1">Shoe care, ironing-only & home linen — all under one roof.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {[
                { name: 'JustPress', sub: 'Ironing only', icon: '👔', href: '/pricing#justpress', bg: 'bg-orange-500', text: 'text-white' },
                { name: 'Shoe Care', sub: 'Antimicrobial + steam cleaning', icon: '👟', href: '/pricing#shoe-care', bg: 'bg-blue-600', text: 'text-white' },
                { name: 'Home & Linen', sub: 'Blankets, curtains, carpets', icon: '🏠', href: '/pricing#home', bg: 'bg-violet-600', text: 'text-white' },
              ].map((s) => (
                <button key={s.name} onClick={() => navigate(s.href)}
                  className={`${s.bg} ${s.text} rounded-2xl px-7 py-5 text-center hover:opacity-90 hover:-translate-y-1 transition-all shadow-md flex flex-col items-center gap-1 flex-1`}>
                  <span className="text-2xl">{s.icon}</span>
                  <div className="text-lg font-black">{s.name}</div>
                  <div className="text-sm opacity-80">{s.sub}</div>
                  <div className="text-xs font-semibold mt-1 opacity-70">See pricing →</div>
                </button>
              ))}
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="py-24 px-4 bg-gradient-to-br from-blue-50 via-slate-50 to-violet-50">
        <div className="max-w-6xl mx-auto">
          <FadeIn className="text-center mb-16">
            <span className="inline-block bg-blue-600 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Process</span>
            <h2 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-4xl sm:text-5xl font-black text-slate-900 mb-4">
              Laundry in{' '}
              <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">4 Simple Steps</span>
            </h2>
            <p className="text-slate-500 text-lg max-w-lg mx-auto">From booking to delivery — designed to be effortless.</p>
          </FadeIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <FadeIn key={step.step} delay={i * 100}>
                  <div className="relative bg-white rounded-3xl p-6 shadow-sm border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                    {i < steps.length - 1 && (
                      <div className="hidden lg:block absolute top-10 left-full w-6 h-0.5 bg-gradient-to-r from-slate-300 to-transparent z-10" />
                    )}
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${step.color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="text-4xl font-black text-slate-100 select-none" style={{ fontFamily: 'Manrope, sans-serif' }}>{step.step}</span>
                    </div>
                    <h3 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{step.desc}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <FadeIn className="text-center mb-16">
            <span className="inline-block bg-orange-100 text-orange-700 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Why JustFold</span>
            <h2 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-4xl sm:text-5xl font-black text-slate-900 mb-4">
              Premium Quality,<br />
              <span className="bg-gradient-to-r from-orange-500 to-orange-400 bg-clip-text text-transparent">Pocket-Friendly Price</span>
            </h2>
          </FadeIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => {
              const Icon = f.icon;
              const [col] = f.color.split(' ');
              return (
                <FadeIn key={f.title} delay={i * 70}>
                  <div className={`group rounded-2xl p-6 border ${f.color} hover:-translate-y-1 hover:shadow-md transition-all duration-300 cursor-default`}>
                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-4 ${col}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-lg font-bold text-slate-900 mb-2">{f.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <FadeIn className="text-center mb-12">
            <span className="inline-block bg-violet-100 text-violet-700 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Happy Customers</span>
            <h2 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-4xl font-black text-slate-900">
              What residents are saying
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { text: '"JustFold made my laundry routine effortless. Fresh clothes every week without any effort."', name: 'Priya S.', pg: 'Sunrise PG, Gurgaon', color: 'border-blue-200 bg-blue-50' },
              { text: '"The FoldPass plan is perfect for me. Fixed cost, regular pickup — no thinking required!"', name: 'Amit R.', pg: 'Green Valley PG', color: 'border-orange-200 bg-orange-50' },
              { text: '"FlashFold saved me before an important meeting. Next-day delivery is a lifesaver."', name: 'Sneha K.', pg: 'City Nest Co-living', color: 'border-violet-200 bg-violet-50' },
            ].map((t, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div className={`rounded-2xl p-6 border-2 ${t.color}`}>
                  <div className="flex gap-1 mb-3">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-orange-400 fill-orange-400" />)}</div>
                  <p className="text-slate-700 text-sm leading-relaxed mb-4 italic">{t.text}</p>
                  <div>
                    <p className="font-bold text-slate-900 text-sm">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.pg}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 px-4 bg-gradient-to-br from-blue-600 via-blue-700 to-violet-700">
        <FadeIn>
          <div className="max-w-3xl mx-auto text-center text-white">
            <div className="inline-flex items-center gap-2 bg-white/15 border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
              <span className="text-sm font-medium">500+ happy residents and counting</span>
            </div>
            <h2 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-4xl sm:text-5xl font-black mb-4">
              Ready to simplify<br />your laundry?
            </h2>
            <p className="text-blue-100 text-lg mb-10 max-w-lg mx-auto">
              First pickup takes less than 60 seconds to book. Join Gurgaon's favourite laundry service.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/customer/login')}
                className="group inline-flex items-center justify-center gap-2 bg-orange-400 hover:bg-orange-500 text-white font-bold text-base px-8 py-4 rounded-2xl transition-all hover:shadow-xl hover:shadow-orange-400/40 hover:-translate-y-1"
              >
                Get Started Free
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ── FOOTER ── */}
      <footer id="contact" className="bg-slate-900 text-white py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <img src="/justfold-logo-transparent.png" alt="JustFold" className="h-9 mb-4" style={{ filter: 'brightness(0) invert(1)' }} />
              <p className="text-slate-400 text-sm leading-relaxed max-w-xs mb-6">
                Professional laundry for PG and co-living residents. Premium quality. Pocket-friendly plans.
              </p>
              <div className="flex items-center gap-3">
                <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center text-slate-400 hover:text-white hover:bg-orange-500 transition-all">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href="https://wa.me/919821381888" target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center text-slate-400 hover:text-white hover:bg-green-500 transition-all">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.112.549 4.1 1.508 5.832L0 24l6.336-1.49A11.942 11.942 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.885 0-3.65-.487-5.19-1.342l-.37-.22-3.85.905.923-3.768-.241-.386A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
                  </svg>
                </a>
              </div>
            </div>
            <div>
              <h4 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-sm font-bold text-white mb-4">Services</h4>
              <ul className="space-y-2">
                {['FoldPass', 'FlexFold', 'FlashFold', 'JustPress', 'Shoe Cleaning'].map(s => (
                  <li key={s}><button onClick={() => navigate('/customer/login')} className="text-sm text-slate-400 hover:text-orange-400 transition-colors">{s}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-sm font-bold text-white mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-sm text-slate-400">
                  <MapPin className="w-4 h-4 mt-0.5 text-slate-600 flex-shrink-0" />
                  Patel Nagar, Gurgaon,<br />Haryana 122001
                </li>
                <li><a href="tel:+919821381888" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"><Phone className="w-4 h-4 text-slate-600" />+91 9821381888</a></li>
                <li><a href="mailto:contact@justfold.in" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"><Mail className="w-4 h-4 text-slate-600" />contact@justfold.in</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-600">© 2025 JustFold. All rights reserved.</p>
            <div className="flex items-center gap-2">
              <button onClick={() => navigate('/login')} className="text-xs text-slate-600 hover:text-slate-400 transition-colors">Admin Portal</button>
              <span className="text-slate-700">·</span>
              <a href="https://www.justfold.in" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-600 hover:text-slate-400 transition-colors">justfold.in</a>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fadeInDown { from { opacity:0; transform:translateY(-16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes fadeInUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
    </div>
  );
};

export default LandingPage;
